import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import AppShell from '../components/AppShell';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { cn } from '../lib/utils';
import { Sun, ArrowRight, ChevronLeft, ChevronRight, ExternalLink, History, GitCompare, Sparkles, Clock } from 'lucide-react';
import { AskAnchorButton } from '../components/AskAnchorButton';
import { AskAnchorPanel } from '../components/AskAnchorPanel';
import { ThinkingIndicator } from '../components/ThinkingIndicator';
import { getToolIcon } from '../lib/tool-icons';
import InsightsSummaryCard from '../components/home/InsightsSummaryCard';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function formatShortDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function MorningScanPage() {
  const navigate = useNavigate();

  // State Management
  const [projects, setProjects] = useState([]);
  const [scanDataMap, setScanDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBriefIndex, setSelectedBriefIndex] = useState(null);
  const [greeting, setGreeting] = useState('Good morning');
  const [isAskAnchorOpen, setIsAskAnchorOpen] = useState(false);
  const [panelLayout, setPanelLayout] = useState('panel');
  const [isBriefingScrolled, setIsBriefingScrolled] = useState(false);
  const briefingScrollRef = useRef(null);

  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [selectedProjectForHistory, setSelectedProjectForHistory] = useState(null);
  const [historyBriefings, setHistoryBriefings] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [compareSelection, setCompareSelection] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Greeting based on time of day
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  // Fetch projects, then fetch scans for all projects
  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectsRes = await api.get('/projects');
        setProjects(projectsRes.data);

        if (projectsRes.data.length === 0) {
          setLoading(false);
          return;
        }

        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const results = await Promise.allSettled(
          projectsRes.data.map(p =>
            api.get(`/projects/${p.id}/morning-scan`, {
              params: { since, save_result: true, generate_narrative: true }
            }).then(res => ({ projectId: p.id, data: res.data }))
          )
        );

        const map = {};
        results.forEach(r => {
          if (r.status === 'fulfilled') {
            map[r.value.projectId] = r.value.data;
          }
        });
        setScanDataMap(map);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load daily briefing');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch briefing history for a project
  const fetchHistory = useCallback(async (projectId, offset = 0) => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/daily-briefings`, {
        params: { limit: 10, offset },
      });
      if (offset === 0) {
        setHistoryBriefings(res.data.briefings || []);
      } else {
        setHistoryBriefings(prev => [...prev, ...(res.data.briefings || [])]);
      }
      setHistoryHasMore(res.data.has_more || false);
      setHistoryOffset(offset + (res.data.briefings?.length || 0));
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load briefing history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const openHistory = useCallback((projectId) => {
    setSelectedProjectForHistory(projectId);
    setShowHistory(true);
    setCompareSelection([]);
    setCompareResult(null);
    setHistoryOffset(0);
    fetchHistory(projectId, 0);
  }, [fetchHistory]);

  const closeHistory = useCallback(() => {
    setShowHistory(false);
    setSelectedProjectForHistory(null);
    setHistoryBriefings([]);
    setCompareSelection([]);
    setCompareResult(null);
  }, []);

  const toggleCompareSelection = useCallback((briefingKey) => {
    setCompareSelection(prev => {
      if (prev.includes(briefingKey)) {
        return prev.filter(k => k !== briefingKey);
      }
      if (prev.length >= 2) {
        return [prev[1], briefingKey];
      }
      return [...prev, briefingKey];
    });
    setCompareResult(null);
  }, []);

  const runComparison = useCallback(async () => {
    if (compareSelection.length !== 2 || !selectedProjectForHistory) return;
    setCompareLoading(true);
    try {
      const res = await api.post(`/projects/${selectedProjectForHistory}/daily-briefings/compare`, {
        briefing_key_a: compareSelection[0],
        briefing_key_b: compareSelection[1],
      });
      setCompareResult(res.data);
    } catch (error) {
      console.error('Error comparing briefings:', error);
      toast.error('Failed to compare briefings');
    } finally {
      setCompareLoading(false);
    }
  }, [compareSelection, selectedProjectForHistory]);

  // Scroll Detection
  useEffect(() => {
    const container = briefingScrollRef.current;
    if (!container || selectedBriefIndex !== null) return;
    const handleScroll = () => {
      setIsBriefingScrolled(container.scrollTop > 40);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedBriefIndex]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedBriefIndex === null) return;
      if (e.key === 'ArrowLeft') goToPrevBrief();
      if (e.key === 'ArrowRight') goToNextBrief();
      if (e.key === 'Escape') closeBrief();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBriefIndex]);

  // Data Transformation
  const briefingCards = useMemo(() => {
    const cards = [];
    Object.entries(scanDataMap).forEach(([projectId, scan]) => {
      const project = projects.find(p => p.id === projectId);
      if (scan.narrative) {
        cards.push({
          id: `${projectId}-narrative`,
          type: 'update',
          title: `${scan.project_name}: Daily Summary`,
          summary: scan.narrative.split('\n')[0],
          fullDescription: scan.narrative,
          projectName: scan.project_name,
          projectId,
          projectColor: project?.color || '#3b82f6',
          sources: scan.sources || [],
          timestamp: scan.timestamp
        });
      }
      (scan.sources || []).forEach(source => {
        (source.highlights || []).forEach(highlight => {
          cards.push({
            id: `${projectId}-${source.source_id}-${highlight.title}`,
            type: highlight.type || 'update',
            title: highlight.title,
            summary: highlight.description,
            fullDescription: highlight.description,
            projectName: scan.project_name,
            projectId,
            projectColor: project?.color || '#3b82f6',
            sourceType: source.source_type,
            sourceLabel: source.source_label,
            url: highlight.url,
            sources: scan.sources || [],
            timestamp: highlight.timestamp
          });
        });
      });
    });
    cards.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    return cards;
  }, [scanDataMap, projects]);

  // Handlers
  const dateFormatted = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });

  const selectedBrief = selectedBriefIndex !== null ? briefingCards[selectedBriefIndex] : null;

  const toggleAskAnchor = useCallback(() => {
    setIsAskAnchorOpen(prev => !prev);
  }, []);

  const handleLayoutChange = useCallback((layout) => {
    setPanelLayout(layout);
  }, []);

  const goToPrevBrief = useCallback(() => {
    if (selectedBriefIndex !== null && selectedBriefIndex > 0) {
      setSelectedBriefIndex(selectedBriefIndex - 1);
    }
  }, [selectedBriefIndex]);

  const goToNextBrief = useCallback(() => {
    if (selectedBriefIndex !== null && selectedBriefIndex < briefingCards.length - 1) {
      setSelectedBriefIndex(selectedBriefIndex + 1);
    }
  }, [selectedBriefIndex, briefingCards.length]);

  const closeBrief = useCallback(() => {
    setSelectedBriefIndex(null);
    setIsAskAnchorOpen(false);
  }, []);

  // Helper Functions
  const getCardColor = (type) => {
    switch (type) {
      case 'new':
        return 'bg-green-500/10';
      case 'updated':
      case 'update':
        return 'bg-blue-500/10';
      case 'stalled':
        return 'bg-amber-500/10';
      case 'risk':
        return 'bg-red-500/10';
      default:
        return 'bg-violet-500/10';
    }
  };

  const getSourceColor = (type) => {
    switch (type) {
      case 'slack':
        return 'bg-[#4A154B]/10 text-[#4A154B] dark:bg-[#4A154B]/20 dark:text-[#E01E5A]';
      case 'github':
        return 'bg-gray-100/50 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200';
      case 'figma':
        return 'bg-[#F24E1E]/10 text-[#F24E1E]';
      case 'notion':
        return 'bg-gray-100/50 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200';
      case 'jira':
        return 'bg-[#0052CC]/10 text-[#0052CC]';
      case 'linear':
        return 'bg-[#5E6AD2]/10 text-[#5E6AD2]';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getSourceLabel = (type) => {
    switch (type) {
      case 'slack':
        return 'Slack';
      case 'github':
        return 'GitHub';
      case 'figma':
        return 'Figma';
      case 'notion':
        return 'Notion';
      case 'jira':
        return 'Jira';
      case 'linear':
        return 'Linear';
      case 'google_docs':
      case 'google-docs':
        return 'Google Docs';
      default:
        return type;
    }
  };

  // History panel for a project
  const historyProjectName = selectedProjectForHistory
    ? projects.find(p => p.id === selectedProjectForHistory)?.name || 'Project'
    : '';

  // HISTORY VIEW
  if (showHistory) {
    return (
      <AppShell>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto pb-32">
            {/* Header */}
            <div className="sticky top-0 z-10 px-10 py-3 backdrop-blur-xl bg-white/40 dark:bg-neutral-900/40">
              <div className="flex items-center justify-between">
                <button
                  onClick={closeHistory}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to briefings
                </button>
                {compareSelection.length === 2 && (
                  <Button
                    size="sm"
                    className="gap-2 rounded-xl"
                    onClick={runComparison}
                    disabled={compareLoading}
                  >
                    <GitCompare className="h-4 w-4" />
                    {compareLoading ? 'Comparing...' : 'Compare Selected'}
                  </Button>
                )}
              </div>
            </div>

            <main className="px-10 py-6 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <History className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-xl font-bold">Briefing History</h2>
                <Badge variant="glass">{historyProjectName}</Badge>
              </div>

              {compareSelection.length > 0 && (
                <p className="text-xs text-muted-foreground mb-4">
                  Select {2 - compareSelection.length} more briefing{2 - compareSelection.length !== 1 ? 's' : ''} to compare
                </p>
              )}

              {/* Compare Result */}
              {compareResult && (
                <Card className="rounded-[24px] p-6 mb-6 glass-elevated animate-fade-up">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <GitCompare className="h-5 w-5 text-violet-500" />
                    Comparison Results
                  </h3>
                  <InsightsSummaryCard insights={compareResult} />
                </Card>
              )}

              {/* Briefing List */}
              <div className="space-y-3">
                {historyBriefings.map((briefing, index) => {
                  const isSelected = compareSelection.includes(briefing.key);
                  return (
                    <Card
                      key={briefing.key}
                      className={cn(
                        'rounded-[20px] p-4 glass animate-fade-up transition-all cursor-pointer',
                        isSelected && 'ring-2 ring-violet-500/50 bg-violet-500/5',
                      )}
                      style={{ animationDelay: `${index * 40}ms` }}
                    >
                      <div className="flex items-center gap-4">
                        {/* Compare checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCompareSelection(briefing.key);
                          }}
                          className={cn(
                            'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                            isSelected
                              ? 'bg-violet-500 border-violet-500 text-white'
                              : 'border-muted-foreground/30 hover:border-violet-500/50'
                          )}
                        >
                          {isSelected && <span className="text-xs font-bold">{compareSelection.indexOf(briefing.key) + 1}</span>}
                        </button>

                        {/* Content */}
                        <div
                          className="flex-1 min-w-0"
                          onClick={() => navigate(`/projects/${selectedProjectForHistory}/briefings/${briefing.key}`)}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold">
                              {formatShortDate(briefing.generated_at)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatRelativeTime(briefing.generated_at)}
                            </span>
                            {briefing.narrative_status === 'failed' && (
                              <Badge variant="outline" className="text-xs text-red-500">Failed</Badge>
                            )}
                            {briefing.insight_status === 'completed' && (
                              <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                            )}
                          </div>
                          {briefing.narrative && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {briefing.narrative}
                            </p>
                          )}
                        </div>

                        <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Load More */}
              {historyHasMore && (
                <div className="flex justify-center mt-6">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => fetchHistory(selectedProjectForHistory, historyOffset)}
                    disabled={historyLoading}
                  >
                    {historyLoading ? 'Loading...' : 'Load more'}
                  </Button>
                </div>
              )}

              {historyBriefings.length === 0 && !historyLoading && (
                <Card className="rounded-[24px] p-12 text-center glass-subtle">
                  <p className="text-muted-foreground">No briefing history yet</p>
                </Card>
              )}

              {historyLoading && historyBriefings.length === 0 && (
                <div className="flex items-center justify-center py-12">
                  <ThinkingIndicator category="scanning" size="lg" />
                </div>
              )}
            </main>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col h-full overflow-hidden">
        {selectedBrief ? (
          // DETAIL VIEW
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex flex-1 min-h-0 overflow-hidden">
              {panelLayout !== 'fullscreen' && (
                <div className="flex-1 overflow-y-auto pb-32">
                  {/* Sub-navigation */}
                  <div className="sticky top-0 z-10">
                    <div className="px-10 py-3">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={closeBrief}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Back to briefings
                        </button>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={goToPrevBrief}
                            disabled={selectedBriefIndex === 0}
                            className="gap-1 rounded-xl"
                          >
                            <ChevronLeft className="h-4 w-4" /> Prev
                          </Button>
                          <span className="text-sm text-muted-foreground tabular-nums">
                            {selectedBriefIndex + 1} / {briefingCards.length}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={goToNextBrief}
                            disabled={selectedBriefIndex === briefingCards.length - 1}
                            className="gap-1 rounded-xl"
                          >
                            Next <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <main className="px-10 py-6 pb-40" key={selectedBriefIndex}>
                    {/* Brief Content Card */}
                    <div
                      className={cn(
                        'rounded-[24px] p-8 mb-8 glass transition-all duration-500 animate-fade-up',
                        getCardColor(selectedBrief.type)
                      )}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <Badge variant="glass" className="text-xs font-medium">
                          {selectedBrief.projectName}
                        </Badge>
                      </div>
                      <h1 className="text-3xl font-bold mb-4 text-foreground/90">
                        {selectedBrief.title}
                      </h1>
                      <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                        {selectedBrief.fullDescription}
                      </p>
                      {/* Footer with source icons + Ask Anchor */}
                      <div className="flex items-center justify-between pt-6 border-t border-white/10">
                        <div className="flex items-center gap-2">
                          {selectedBrief.sources &&
                            selectedBrief.sources.map((source) => {
                              const Icon = getToolIcon(source.source_type);
                              return Icon ? (
                                <div
                                  key={source.source_id}
                                  className={cn(
                                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium backdrop-blur-md',
                                    getSourceColor(source.source_type)
                                  )}
                                >
                                  <Icon className="h-3.5 w-3.5" />
                                  {getSourceLabel(source.source_type)}
                                </div>
                              ) : null;
                            })}
                        </div>
                        <AskAnchorButton
                          size="sm"
                          active={isAskAnchorOpen}
                          onClick={toggleAskAnchor}
                        />
                      </div>
                    </div>

                    {/* Related Sources Section */}
                    {selectedBrief.sources && selectedBrief.sources.length > 0 && (
                      <div className="animate-fade-up" style={{ animationDelay: '80ms' }}>
                        <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                          Related Sources
                          <span className="text-xs font-normal text-muted-foreground">
                            {selectedBrief.sources.length}{' '}
                            {selectedBrief.sources.length === 1 ? 'source' : 'sources'}
                          </span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedBrief.sources.map((source, index) => {
                            const SourceIcon = getToolIcon(source.source_type);
                            return (
                              <div
                                key={source.source_id || index}
                                className="glass rounded-[20px] p-5 hover:bg-white/10 transition-colors animate-fade-up"
                                style={{ animationDelay: `${120 + index * 60}ms` }}
                              >
                                <div className="flex items-center gap-3 mb-3">
                                  <div
                                    className={cn(
                                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium backdrop-blur-md',
                                      getSourceColor(source.source_type)
                                    )}
                                  >
                                    {SourceIcon && <SourceIcon className="h-3.5 w-3.5" />}
                                    {getSourceLabel(source.source_type)}
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {source.total_changes} change
                                    {source.total_changes !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <h3 className="text-sm font-medium text-foreground/90 mb-2">
                                  {source.source_label}
                                </h3>
                                {source.highlights && source.highlights.length > 0 && (
                                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                                    {source.highlights.map((h) => h.title).join('. ')}
                                  </p>
                                )}
                                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                                  <span className="text-xs text-muted-foreground">
                                    {source.highlights?.length || 0} highlight
                                    {(source.highlights?.length || 0) !== 1 ? 's' : ''}
                                  </span>
                                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </main>
                </div>
              )}

              <AskAnchorPanel
                isOpen={isAskAnchorOpen}
                onClose={toggleAskAnchor}
                context={{ projectName: selectedBrief.projectName, topic: selectedBrief.title }}
                layout={panelLayout}
                onLayoutChange={handleLayoutChange}
              />
            </div>
          </div>
        ) : (
          // GRID VIEW
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {panelLayout !== 'fullscreen' && (
              <div className="flex-1 overflow-y-auto pb-32" ref={briefingScrollRef}>
                <main className="px-10 py-6 pb-40">
                  {/* Sticky Greeting Header */}
                  <div
                    className={cn(
                      'sticky top-0 z-10 -mx-10 px-10 transition-all duration-300',
                      isBriefingScrolled
                        ? 'py-3 backdrop-blur-xl bg-white/40 dark:bg-neutral-900/40'
                        : 'pt-0 pb-6 bg-transparent'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        {!isBriefingScrolled && (
                          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 font-medium">
                            Daily Brief
                          </p>
                        )}
                        <h1
                          className={cn(
                            'font-bold tracking-tight text-foreground/90 transition-all duration-300',
                            isBriefingScrolled ? 'text-lg' : 'text-3xl'
                          )}
                        >
                          {greeting}
                        </h1>
                      </div>
                      <div
                        className={cn(
                          'flex items-center gap-2',
                          !isBriefingScrolled && 'flex-col items-end'
                        )}
                      >
                        {!isBriefingScrolled && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {dateFormatted}
                          </p>
                        )}
                        <AskAnchorButton
                          size="sm"
                          active={isAskAnchorOpen}
                          onClick={toggleAskAnchor}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Loading State */}
                  {loading && (
                    <div className="flex items-center justify-center py-20">
                      <ThinkingIndicator category="scanning" size="lg" />
                    </div>
                  )}

                  {/* Empty State */}
                  {!loading && briefingCards.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="h-16 w-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4">
                        <Sun className="h-8 w-8 text-amber-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground/90 mb-2">
                        No briefings yet
                      </h3>
                      <p className="text-muted-foreground max-w-md">
                        Your daily briefing will appear here once your projects have
                        connected sources with recent activity.
                      </p>
                    </div>
                  )}

                  {/* Bento Grid */}
                  {!loading && briefingCards.length > 0 && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
                        {briefingCards.map((card, index) => {
                          const SourceIcon = card.sourceType
                            ? getToolIcon(card.sourceType)
                            : null;

                          return (
                            <article
                              key={card.id}
                              className={cn(
                                'group rounded-[24px] p-6 cursor-pointer transition-all duration-300',
                                'hover:shadow-glass-lg hover:-translate-y-1 glass animate-fade-up',
                                getCardColor(card.type)
                              )}
                              style={{ animationDelay: `${index * 60}ms` }}
                              onClick={() => setSelectedBriefIndex(index)}
                            >
                              <h3 className="text-lg font-bold text-foreground mb-3 leading-tight">
                                {card.title}
                              </h3>
                              <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                                {card.summary}
                              </p>
                              <div className="mb-4">
                                <Badge variant="glass" className="text-xs font-medium">
                                  {card.projectName}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-foreground flex items-center gap-1 group-hover:gap-2 transition-all">
                                  Read more <ArrowRight className="h-4 w-4" />
                                </span>
                                {SourceIcon && (
                                  <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center text-foreground/70 backdrop-blur-sm">
                                    <SourceIcon className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                            </article>
                          );
                        })}
                      </div>

                      {/* History buttons per project */}
                      {projects.length > 0 && (
                        <div className="mt-8 space-y-2">
                          <h3 className="text-sm font-semibold text-muted-foreground">Briefing History</h3>
                          <div className="flex flex-wrap gap-2">
                            {projects.map(p => (
                              <Button
                                key={p.id}
                                variant="ghost"
                                size="sm"
                                className="gap-2 rounded-xl glass"
                                onClick={() => openHistory(p.id)}
                              >
                                <History className="h-4 w-4" />
                                {p.name}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </main>
              </div>
            )}
            <AskAnchorPanel
              isOpen={isAskAnchorOpen}
              onClose={toggleAskAnchor}
              context={{ projectName: 'Daily Briefing' }}
              layout={panelLayout}
              onLayoutChange={handleLayoutChange}
            />
          </div>
        )}
      </div>
    </AppShell>
  );
}
